import LoadingCircle from '@cpg-its/ads.common.loading-circle';
import React, { Suspense } from 'react';
import { Route, Switch, useRouteMatch } from 'react-router-dom';
import { ForbiddenErrorMessage } from '../../../../global/components/forbidden-error-message';
import { withForbidden } from '../../../../global/components/HOCs/with-forbidden';
import { withLayout } from '../../../../global/components/HOCs/with-layout';
import { GpsType, LayoutModels } from '../../../../global/components/layout/layout-models';
import { PageFactory } from '../../../../global/components/page-factory';
import { TcsmAppConfigs } from '../../../../global/models/configuration-models';
import { TcsmFeatures } from '../../../../global/models/features-model';
import { OfficerAdminAppKeys, OfficerAdminContext } from '../../../../global/models/officer-admin-model';
import { PersonManagementApp } from '../../../account-info/components/person-management-app';
import { useAnnualEnrollmentSettings } from '../../../annual-enrollment/hooks/useAnnualEnrollmentSettings';
import { useRoleObject } from '../../components/useRoles';
import { JuniorGroupPlanAdmin, SeniorGroupPlanAdmin } from '../../constants/constant-strings';
import { ArcRoutes } from '../../constants/routes';
import { useGroupsBillingSettings } from '../../hooks/useGroupsBillingSettings';
import { BillingAccountsSearch } from '../billing-accounts/billing-accounts-search';
import { DocumentDownload } from '../document-download/document-download';
import { GroupAdminSearch } from '../institution-officers/group-admins-search';
import { ParticipantsSearch } from '../participants/participants-search';
import { GroupSearch } from './group-search';
import { GroupsLanding } from './groups-landing';

const GroupSearchComponent = withLayout(null, GpsType.GROUP_LIST)(withForbidden(TcsmFeatures.GroupSearch)(GroupSearch));
const GroupsLandingComponent = withForbidden(TcsmFeatures.GroupsLanding)(GroupsLanding);
const BillingAccountSearchComponent = withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.EmployeeSearch)(BillingAccountsSearch));
const ParticipantsSearchComponent = withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.EmployeeSearch)(ParticipantsSearch));
const GroupAdminsSearchComponent = withLayout(LayoutModels.FULL_WIDTH)(withForbidden(TcsmFeatures.GroupAdminSearch)(GroupAdminSearch));
const DocDownload = withLayout(null, GpsType.DOWNLOADS)(withForbidden(TcsmFeatures.DocDownload)(DocumentDownload));

const ReportsBenefitsGroup = React.lazy(() => import('../billing-accounts/reports/reports-benefits-group'));
const ReportsBillingAccount = React.lazy(() => import('../billing-accounts/reports/reports-billing-account'));
const ReportsBenefitsGroupWrapper = withLayout(LayoutModels.NO_LAY_OUT)(ReportsBenefitsGroup);
const ReportsBillingAccountWrapper = withLayout(LayoutModels.NO_LAY_OUT)(ReportsBillingAccount);
const BenefitGroupChangeLog = React.lazy(() => import('../../../../backend-search/benefit-group-change-log/benefit-group-change-log-search'));

interface Props {
	appConfig: TcsmAppConfigs;
}
export function GroupsContainer(props: Props) {

	const { appConfig } = props;
	const annualEnrollmentSettingsLoaded = useAnnualEnrollmentSettings(appConfig?.annualEnrollmentSettingsId);
	const roles = useRoleObject();
	const routeMatch = useRouteMatch();

	const settingsLoaded = useGroupsBillingSettings(appConfig?.groupsBillingSettingsId);

	// restrict access to group features by roles
	if (!(roles?.mlps?.map(a => a.role_name)?.includes(JuniorGroupPlanAdmin)) && !(roles?.mlps?.map(a => a.role_name)?.includes(SeniorGroupPlanAdmin))) {
		return <ForbiddenErrorMessage />;
	}

	if (!settingsLoaded || !annualEnrollmentSettingsLoaded) {
		return <LoadingCircle />;
	}

	return (
		<>
			<Suspense fallback={<LoadingCircle />}>
				<Switch>
					<Route exact path={routeMatch.path} render={() => <GroupsLandingComponent />} />
					<Route exact path={`${routeMatch.path}${ArcRoutes.GroupSearch}`} render={() => <GroupSearchComponent />} />
					<Route exact path={`${routeMatch.path}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`} render={() => <ParticipantsSearchComponent />} />
					<Route
						exact
						path={`${routeMatch.path}${ArcRoutes.GroupAdminSearch}`}
						render={() => (
							<OfficerAdminContext.Provider value={OfficerAdminAppKeys.groupAdmin}>
								<GroupAdminsSearchComponent />
							</OfficerAdminContext.Provider>
						)} />
					{ /*<Route exact path={`${routeMatch.path}${ArcRoutes.GroupAdminAdd}`} render={() => <AddAdminComponent />} />*/}
					<Route exact path={`${routeMatch.path}${ArcRoutes.GroupDownloads}`} render={() => <DocDownload />} />
					<Route
						path={[`${routeMatch.path}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.Participants}`,
						`${routeMatch.path}${ArcRoutes.GroupAdministrators}`]}
						render={() => (
							<PersonManagementApp
								appConfig={appConfig}
								isArc
							/>
						)}
					/>
					<Route exact path={`${routeMatch.path}${ArcRoutes.ReportsBenefitsGroup}`}>
						<ReportsBenefitsGroupWrapper groupsBillingSettingsId={appConfig.groupsBillingSettingsId} />
					</Route>
					<Route exact path={`${routeMatch.path}${ArcRoutes.BillingAccountsSearch}${ArcRoutes.ReportsBillingAccount}`}>
						<ReportsBillingAccountWrapper groupsBillingSettingsId={appConfig.groupsBillingSettingsId} />
					</Route>
					<Route exact path={`${routeMatch.path}${ArcRoutes.BillingAccountsSearch}`} render={() => <BillingAccountSearchComponent />} />
					<Route exact path={`${routeMatch.path}${ArcRoutes.BenefitGroupChangeLog}`} render={() => <BenefitGroupChangeLog />} />
					<Route path={`${routeMatch.path}/*`} render={() => <PageFactory />} />
				</Switch>
			</Suspense>
		</>

	);

}

